import React, { useCallback, useState } from 'react';
import {
  MUSICIAN_BACKGROUND_OPTIONS,
  MUSICIAN_DRESS_OPTIONS,
  MUSICIAN_SLEEVE_OPTIONS,
  MUSICIAN_ITEM_OPTIONS,
  MUSICIAN_POSE_OPTIONS,
} from '../constants/musician';
import { MagicWandIcon, ResetIcon, CloseIcon, UploadIcon } from './icons';

type ImageData = { data: string; mimeType: string } | null;

interface MusicianControlPanelProps {
  onGenerate: (customOutfit: ImageData, customBackground: ImageData) => void;
  onReset: () => void;
  isLoading: boolean;
  error: string | null;
  remainingCredits: number;
  scrollToPlanInfo?: () => void;

  backgroundId: string;
  onBackgroundIdChange: (id: string) => void;
  customBackgroundText: string;
  onCustomBackgroundTextChange: (s: string) => void;
  customBackgroundImage: ImageData;
  onCustomBackgroundImageChange: (v: ImageData) => void;

  dressId: string;
  onDressIdChange: (id: string) => void;
  customDressText: string;
  onCustomDressTextChange: (s: string) => void;
  customDressImage: ImageData;
  onCustomDressImageChange: (v: ImageData) => void;

  sleeveId: string;
  onSleeveIdChange: (id: string) => void;
  customSleeveText: string;
  onCustomSleeveTextChange: (s: string) => void;

  itemId: string;
  onItemIdChange: (id: string) => void;
  customItemText: string;
  onCustomItemTextChange: (s: string) => void;

  poseId: string;
  onPoseIdChange: (id: string) => void;
}

const ImageUploaderBlock: React.FC<{
  label: string;
  onUpload: (data: string, mimeType: string) => void;
  currentImage: ImageData;
  onClear: () => void;
}> = ({ label, onUpload, currentImage, onClear }) => {
  const [err, setErr] = useState<string | null>(null);
  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
        setErr('JPEG / PNG / WebP でお願いします');
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        setErr('5MB以下にしてください');
        return;
      }
      setErr(null);
      const r = new FileReader();
      r.onload = () => {
        const s = r.result as string;
        if (s) onUpload(s, f.type);
      };
      r.readAsDataURL(f);
    },
    [onUpload]
  );
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" id={`upload-${label}`} />
      {!currentImage ? (
        <label htmlFor={`upload-${label}`} className="cursor-pointer block">
          <UploadIcon className="w-6 h-6 text-slate-400 mx-auto mb-1" />
          <p className="text-slate-600 text-sm">{label}</p>
          {err && <p className="text-red-500 text-xs mt-1">{err}</p>}
        </label>
      ) : (
        <div className="relative inline-block">
          <img src={currentImage.data} alt="" className="h-24 object-cover rounded border" />
          <button
            type="button"
            onClick={onClear}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
            aria-label="削除"
          >
            <CloseIcon className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export const MusicianControlPanel: React.FC<MusicianControlPanelProps> = ({
  onGenerate,
  onReset,
  isLoading,
  error,
  remainingCredits,
  scrollToPlanInfo,
  backgroundId,
  onBackgroundIdChange,
  customBackgroundText,
  onCustomBackgroundTextChange,
  customBackgroundImage,
  onCustomBackgroundImageChange,
  dressId,
  onDressIdChange,
  customDressText,
  onCustomDressTextChange,
  customDressImage,
  onCustomDressImageChange,
  sleeveId,
  onSleeveIdChange,
  customSleeveText,
  onCustomSleeveTextChange,
  itemId,
  onItemIdChange,
  customItemText,
  onCustomItemTextChange,
  poseId,
  onPoseIdChange,
}) => {
  const handleGenerate = () => {
    onGenerate(customDressImage, customBackgroundImage);
  };

  const isGenerateDisabled =
    isLoading ||
    remainingCredits <= 0 ||
    (backgroundId === 'custom_text' && !customBackgroundText.trim()) ||
    (backgroundId === 'custom_image' && !customBackgroundImage) ||
    (dressId === 'custom_text' && !customDressText.trim()) ||
    (dressId === 'custom_image' && !customDressImage) ||
    (sleeveId === 'custom_text' && !customSleeveText.trim()) ||
    (itemId === 'custom_text' && !customItemText.trim());

  const section = (title: string, children: React.ReactNode) => (
    <div>
      <h3 className="text-base font-semibold text-slate-800 mb-3">{title}</h3>
      {children}
    </div>
  );

  const select = (
    value: string,
    onChange: (id: string) => void,
    options: { id: string; name: string }[],
    onClear?: () => void
  ) => (
    <select
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v);
        if (onClear && (v === 'custom_text' || v === 'custom_image')) {
          // スイッチ時にクリアは親でやる想定
        }
      }}
      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 shadow-sm space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900">音楽家用スタイル</h2>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-600"
          aria-label="リセット"
        >
          <ResetIcon className="w-4 h-4" />
          リセット
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          残りクレジット: <span className="font-bold">{remainingCredits}</span> 回
        </p>
        {remainingCredits <= 0 && scrollToPlanInfo && (
          <button type="button" onClick={scrollToPlanInfo} className="text-sm text-blue-600 underline mt-1">
            → プランについて
          </button>
        )}
      </div>

      {section(
        '背景',
        <>
          <div className="mb-3">
            {select(backgroundId, (id) => {
              onBackgroundIdChange(id);
              if (id !== 'custom_image') onCustomBackgroundImageChange(null);
            }, MUSICIAN_BACKGROUND_OPTIONS)}
          </div>
          {backgroundId === 'custom_text' && (
            <textarea
              rows={2}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: コンサートホールの舞台"
              value={customBackgroundText}
              onChange={(e) => onCustomBackgroundTextChange(e.target.value)}
            />
          )}
          {backgroundId === 'custom_image' && (
            <ImageUploaderBlock
              label="背景写真をアップロード"
              onUpload={(data, mime) => onCustomBackgroundImageChange({ data, mimeType: mime })}
              currentImage={customBackgroundImage}
              onClear={() => onCustomBackgroundImageChange(null)}
            />
          )}
        </>
      )}

      {section(
        '衣装',
        <>
          <div className="mb-3">
            {select(dressId, (id) => {
              onDressIdChange(id);
              if (id !== 'custom_image') onCustomDressImageChange(null);
            }, MUSICIAN_DRESS_OPTIONS)}
          </div>
          {dressId === 'custom_text' && (
            <textarea
              rows={2}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: ロングドレス、シルバー"
              value={customDressText}
              onChange={(e) => onCustomDressTextChange(e.target.value)}
            />
          )}
          {dressId === 'custom_image' && (
            <ImageUploaderBlock
              label="衣装写真をアップロード"
              onUpload={(data, mime) => onCustomDressImageChange({ data, mimeType: mime })}
              currentImage={customDressImage}
              onClear={() => onCustomDressImageChange(null)}
            />
          )}
        </>
      )}

      {section(
        '袖のデザイン',
        <>
          <div className="mb-3">
            {select(sleeveId, onSleeveIdChange, MUSICIAN_SLEEVE_OPTIONS)}
          </div>
          {sleeveId === 'custom_text' && (
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: 七分袖"
              value={customSleeveText}
              onChange={(e) => onCustomSleeveTextChange(e.target.value)}
            />
          )}
        </>
      )}

      {section(
        '楽器・アイテムなど',
        <>
          <div className="mb-3">
            {select(itemId, onItemIdChange, MUSICIAN_ITEM_OPTIONS)}
          </div>
          {itemId === 'custom_text' && (
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="例: ギター"
              value={customItemText}
              onChange={(e) => onCustomItemTextChange(e.target.value)}
            />
          )}
        </>
      )}

      {section('顔の向き', (
        <div>
          {select(poseId, onPoseIdChange, MUSICIAN_POSE_OPTIONS)}
        </div>
      ))}

      <button
        type="button"
        onClick={handleGenerate}
        disabled={isGenerateDisabled}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
      >
        <MagicWandIcon className="w-5 h-5" />
        宣材写真を生成
      </button>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
